import { Trans, useTranslation } from "react-i18next"
import { useQuery } from "react-query"
import { useParams, useSearchParams } from "react-router-dom"

import { Grid } from "@mui/material"
import type { AxiosError, AxiosResponse } from "axios"

import { ReasonsBreakdownChart } from "../../../components/graphs/PieChart"
import type { LabelReasonsData } from "../../../types/generated/api/LabelReasonsData"
import type { LabelReasonsInsight } from "../../../types/generated/api/LabelReasonsInsight"
import { RealTimeInsightsAPI } from "../Api"

/**
 * DecisionReasonInsight Component
 *
 * A component that displays a pie chart visualization of decision reasons breakdown.
 * It fetches label reason data for a specific project within a given time range and
 * presents it in a circular chart format.
 *
 * Features:
 * - Shows distribution of different decision reasons
 * - Includes interactive tooltips for better understanding
 * - Handles loading and error states
 * - Provides empty state handling
 *
 * @component
 */
export const DecisionReasonInsight = (): React.ReactElement => {
  // Extract project ID from URL parameters
  const { id: projectId } = useParams<{ id: string }>()

  // Get search params from URL (using _ to indicate unused setSearchParams)
  const [searchParams, _] = useSearchParams()

  // Extract date range from URL query parameters
  const startDate = searchParams.get("startDate")
  const endDate = searchParams.get("endDate")

  const { t } = useTranslation("insights", {
    keyPrefix: "DecisionReasonInsight",
  })

  // Fetch label reasons data using react-query
  const {
    isLoading,
    data: labelReasons,
    isError,
  } = useQuery<AxiosResponse<LabelReasonsInsight>, AxiosError>({
    queryKey: ["real_time_insights", "label_reasons", projectId, startDate, endDate],
    queryFn: () =>
      RealTimeInsightsAPI.fetchLabelReasonInsight({
        project_uuid: projectId as string,
        start_time: startDate as string,
        end_time: endDate as string,
      }),
    // Only fetch if all required parameters are present
    enabled: !!projectId && !!startDate && !!endDate,
  })

  // Check if there's no data to display
  const isEmpty = !labelReasons?.data.data || Object.keys(labelReasons?.data.data).length === 0

  return (
    <Grid item xs={12}>
      <ReasonsBreakdownChart
        title={t("title")}
        tooltipText={<Trans>{t("tooltip")}</Trans>}
        data={labelReasons?.data.data as Record<string, LabelReasonsData> | undefined}
        isLoading={isLoading}
        graphHeight={220}
        emptyState={
          isError
            ? {
                title: t("emptyState.insightFailed"),
              }
            : isEmpty
              ? {
                  title: t("emptyState.noData"),
                }
              : undefined
        }
      />
    </Grid>
  )
}
